import React, { useState, useEffect } from 'react';
import {
  forEach,
  isArray,
  isEmpty,
  isNil,
  isObject,
  isString,
  map,
  some,
  trim,
} from 'lodash';
import Globalizer from '../../../globalizer';
import translateService from '../../../common/translateService';
import { getPriceGroup } from '../../../services/product/productService';
import DisbursementSimulator from '../../DisbursementSimulator.jsx';
import ProductOfferDescription from './ProductOfferDescription.jsx';
import LowerPriceMessage from './LowerPriceMessage.jsx';

import './ProductPrice.scss';

const ProductPrice = ({
  isKit,
  basePrice,
  option,
  qtySelected,
  tagsProps,
  currencySymbol,
  decimalPlaces,
  lowerPrice,
  isKlarnaAvailable,
  culture,
  financingEnabled,
  hasManyPrices,
}) => {
  Globalizer.init();

  const [price, setPrice] = useState({
    priceCalculated: 0,
    discount: 0,
    priceRetail: 0,
    priceGroup: 0,
    fullPriceObject: isKit
      ? basePrice
      : !isEmpty(option)
      ? option.price
      : basePrice,
  });
  const [tags, setTags] = useState([]);

  useEffect(() => {
    const newFullPriceObject = isKit
      ? basePrice
      : !isEmpty(option)
      ? option.price
      : basePrice;
    const g = getPriceGroup(newFullPriceObject, qtySelected);
    const discount = calculateDiscount(
      newFullPriceObject[`price${g}`],
      newFullPriceObject.priceRetail
    );

    setPrice({
      priceCalculated: newFullPriceObject[`price${g}`],
      discount: discount,
      priceRetail: newFullPriceObject.priceRetail,
      priceGroup: g,
      fullPriceObject: newFullPriceObject,
    });
  }, [basePrice]);

  useEffect(() => {
    const g = getPriceGroup(price.fullPriceObject, qtySelected);

    if (g !== price.priceGroup) {
      setPrice((prevPrice) => {
        return {
          ...prevPrice,
          priceGroup: g,
        };
      });
    }
  }, [qtySelected]);

  useEffect(() => {
    const p = isKit ? basePrice : !isEmpty(option) ? option.price : basePrice;

    setPrice((prevPrice) => {
      const newPrice = p[`price${prevPrice.priceGroup}`];
      const discount = calculateDiscount(newPrice, price.priceRetail);

      return {
        ...prevPrice,
        priceCalculated: newPrice,
        discount: discount,
        fullPriceObject: p,
      };
    });
  }, [price.priceGroup]);

  useEffect(() => {
    if (!isNil(option) && !isEmpty(option)) {
      let priceVariation = 0;

      if (isKit) {
        forEach(option, (o) => {
          priceVariation += o.priceVariation;
        });
      } else {
        priceVariation += option.priceVariation;
      }

      setPrice((prevPrice) => {
        const newFullPriceObject = isKit ? basePrice : option.price;
        const newPriceRetail = newFullPriceObject.priceRetail;
        const newPrice =
          newFullPriceObject[`price${prevPrice.priceGroup}`] + priceVariation;
        const discount = calculateDiscount(newPrice, newPriceRetail);

        return {
          ...prevPrice,
          priceCalculated: newPrice,
          priceRetail: newPriceRetail,
          discount: discount,
          fullPriceObject: newFullPriceObject,
        };
      });
    } else {
      setPrice((prevPrice) => {
        const newFullPriceObject = basePrice;
        const newPriceRetail = newFullPriceObject.priceRetail;
        const newPrice = newFullPriceObject[`price${prevPrice.priceGroup}`];
        const discount = calculateDiscount(newPrice, newPriceRetail);

        return {
          ...prevPrice,
          fullPriceObject: newFullPriceObject,
          priceRetail: newPriceRetail,
          priceCalculated: newPrice,
          discount: discount,
        };
      });
    }
  }, [option]);

  useEffect(() => {
    setTags(getTagsArray(tagsProps));
  }, [tagsProps]);

  const getTagsArray = (tags) => {
    try {
      if (isArray(tags)) return tags;

      if (isString(tags)) {
        const serializedData = JSON.parse(tags);
        if (isArray(serializedData)) return serializedData;
        if (isObject(serializedData)) return [serializedData];

        return [];
      }
      if (isObject(tags)) return [tags];

      return [];
    } catch (error) {
      console.error('Error in ProductTags', error);
      return [];
    }
  };

  const shouldShowDiscountDescriptionContainer = (tags) =>
    some(tags, (t) => !isNil(t.descriptionText) && !isEmpty(t.descriptionText));

  const calculateDiscount = (price, retailPrice) => {
    const discount = Math.floor(100 - (price * 100) / retailPrice);
    return discount > 0 ? discount : 0;
  };

  const getFormattedPrice = (price, decimalPlaces) => {
    return price ? Globalizer.formatNumber(price, decimalPlaces) : '';
  };

  const getFormattedDiscount = (discount) => {
    return discount
      ? Math.floor(discount) === 0
        ? ''
        : `-${Math.floor(discount)}%`
      : '';
  };

  const priceRanges = () => {
    const ranges = [];
    for (let i = 0; i < 4; i++) {
      if (price.fullPriceObject[`price${i}`] === 0) {
        break;
      }

      ranges.push(
        <div
          className={trim(
            `prd-price-range ${
              price.fullPriceObject[`price${i}QtaMax`] !== 0
                ? qtySelected >= price.fullPriceObject[`price${i}QtaMin`] &&
                  qtySelected <= price.fullPriceObject[`price${i}QtaMax`]
                  ? 'selected'
                  : ''
                : qtySelected >= price.fullPriceObject[`price${i}QtaMin`]
                ? 'selected'
                : ''
            }`
          )}
          key={i}>
          <div className='prd-price-range-number'>
            {price.fullPriceObject[`price${i}QtaMax`] !== 0
              ? i === 0
                ? translateService.get('productDetail.oneUnit')
                : translateService.interpolate(`productDetail.fromXToY`, {
                    x: price.fullPriceObject[`price${i}QtaMin`],
                    y: price.fullPriceObject[`price${i}QtaMax`],
                  })
              : translateService.interpolate(`productDetail.moreThan`, {
                  x: price.fullPriceObject[`price${i}QtaMin`],
                })}
          </div>
          <div className='prd-price-range-price'>
            {`${currencySymbol} 
                        ${getFormattedPrice(
                          price.fullPriceObject[`price${i}`],
                          decimalPlaces
                        )}`}
          </div>
          <div className='prd-price-range-discount'>
            {getFormattedDiscount(
              calculateDiscount(
                price.fullPriceObject[`price${i}`],
                price.priceRetail
              )
            )}
          </div>
        </div>
      );
    }

    return ranges;
  };

  return (
    <>
      <div className='prd-price'>
        <div className='prd-price-container'>
          <h1>
            {price.priceCalculated
              ? `${
                  !isEmpty(option)
                    ? ''
                    : isKit || hasManyPrices
                    ? translateService.get(
                        'productCard.ProductPriceStartingFrom'
                      )
                    : ''
                } ${currencySymbol} ${getFormattedPrice(
                  price.priceCalculated,
                  decimalPlaces
                )}`
              : ''}
          </h1>
          {price.discount !== 0 ? (
            <div className='prd-price-discount'>
              <h1 className='prd-price-old'>
                {`${currencySymbol} 
                            ${getFormattedPrice(
                              price.priceRetail,
                              decimalPlaces
                            )}`}
              </h1>
              &nbsp;
              <h1 className='prd-price-percentage'>
                {getFormattedDiscount(price.discount)}
              </h1>
            </div>
          ) : (
            <></>
          )}
          <LowerPriceMessage lowerPrice={lowerPrice}></LowerPriceMessage>
          {!isNil(tags) &&
          !isEmpty(tags) &&
          shouldShowDiscountDescriptionContainer(tags) ? (
            <div className='prd-price-discount-description-container'>
              {map(tags, (t, idx) => (
                <ProductOfferDescription key={idx} tag={t} idx={idx} />
              ))}
            </div>
          ) : (
            <></>
          )}
        </div>
        <div className='prd-price-payments-container'>
          {financingEnabled && (
            <DisbursementSimulator
              financingEnabled={financingEnabled}
              currentAmount={price.priceCalculated}
            />
          )}
          {isKlarnaAvailable && (
            <klarna-placement
              id='klarna'
              data-key='credit-promotion-auto-size'
              data-locale={culture}
              data-purchase-amount={Number.parseInt(
                price.priceCalculated * 10 ** decimalPlaces
              )}></klarna-placement>
          )}
          {/* {isPayPalAvailable && (
                    <div className='prd-price-payments'>
                        <div
                            data-pp-message
                            data-pp-style-layout='text'
                            data-pp-style-logo-type='inline'
                            data-pp-style-text-color='black'
                            data-pp-amount={price.priceCalculated}></div>
                    </div>
                )} */}
        </div>
      </div>
      {Boolean(price.fullPriceObject.price1) && (
        <div className='prd-price-ranges'>{priceRanges()}</div>
      )}
    </>
  );
};

export default ProductPrice;
